import { baseApi } from "../api/baseApi";
import { BaseUser } from "../types/common.types";
import { CurrentUser, Employee, User, UserRoleType } from "../types/user.types";

// Backend SecExternalUser interface som representerar den faktiska API-responsen
export interface SecExternalUserResponse {
  ExternalUserNo: number;
  EntityNo?: number;
  ExternalUserFirstName: string;
  ExternalUserLastName: string;
  ExternalUserFullName: string;
  ExternalUserImagePath?: string;
  ExternalUserLastUsed?: string;
  Created: string;
  CreatedBy: number;
  Changed?: string;
  ChangedBy?: number;
  ExternalUserStatus: boolean;
  OrgLegalEntity?: {
    LegalEntityValue: string;
  };
  SecExternalUserRoles?: Array<{
    ExternalUserRoleNo: number;
    ExternalUserRoleTypeNo: number;
    ExternalUserRoleTypeName?: string;
  }>;
  CrmTeamMembers?: Array<{
    TeamMemberNo: number;
    TeamNo: number;
    CrmTeam?: {
      TeamNo: number;
      TeamName: string;
    };
  }>;
}

// Helper-funktion för att få visningsnamn för roller
const getRoleDisplayName = (roleType: UserRoleType): string => {
  switch (roleType) {
    case UserRoleType.Customer:
      return "Kund";
    case UserRoleType.Employee:
      return "Anställd";
    case UserRoleType.Crm:
      return "CRM-användare";
    case UserRoleType.Admin:
      return "Administratör";
    case UserRoleType.SalesPerson:
      return "Säljare";
    case UserRoleType.SalesAdmin:
      return "Säljchef";
    case UserRoleType.Agent:
      return "Agent";
    default:
      return `Roll ${roleType}`;
  }
};

// Helper function to get color for user roles (MUI Chip colors)
const getRoleColor = (
  roleType: UserRoleType
):
  | "default"
  | "primary"
  | "secondary"
  | "error"
  | "info"
  | "success"
  | "warning" => {
  switch (roleType) {
    case UserRoleType.Admin:
    case UserRoleType.CrmAdmin:
    case UserRoleType.AppAdmin:
      return "error";
    case UserRoleType.SalesAdmin:
    case UserRoleType.ExternalSaleAdmin:
      return "warning";
    case UserRoleType.SalesPerson:
    case UserRoleType.ExternalSale:
      return "primary";
    case UserRoleType.Crm:
      return "info";
    case UserRoleType.Employee:
      return "secondary";
    case UserRoleType.Customer:
      return "success";
    case UserRoleType.Agent:
      return "default";
    default:
      return "default";
  }
};

// Transformatorfunktion för att konvertera backend-svar till frontend-format
const transformSecExternalUserToUser = (
  backendResponse: SecExternalUserResponse
): User => {
  return {
    Id: backendResponse.ExternalUserNo,
    Name: backendResponse.ExternalUserFullName,
    GivenName: backendResponse.ExternalUserFirstName,
    OrgId: backendResponse.OrgLegalEntity?.LegalEntityValue || "",
    FirstName: backendResponse.ExternalUserFirstName,
    LastName: backendResponse.ExternalUserLastName,
    ImagePath: backendResponse.ExternalUserImagePath,
    LastUsed: backendResponse.ExternalUserLastUsed,
    Created: backendResponse.Created,
    CreatedBy: backendResponse.CreatedBy,
    Changed: backendResponse.Changed,
    ChangedBy: backendResponse.ChangedBy,
    Status: backendResponse.ExternalUserStatus,
    EntityNo: backendResponse.EntityNo,
  };
};

export const userApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // User management endpoints (from usersApi.ts)
    getUsersWithRole: builder.query<User[], UserRoleType>({
      query: (roleType) => ({
        url: `/v2/crm/user/withrole/${roleType}`,
        method: "GET",
      }),
      transformResponse: (response) => {
        if (!Array.isArray(response)) {
          console.warn(
            "Oväntad responsformat från getUsersWithRole:",
            response
          );
          return [];
        }

        return response.map((user) => ({
          Id: user.Id || user.ExternalUserNo,
          Name:
            user.Name ||
            user.ExternalUserFullName ||
            `${user.FirstName || ""} ${user.LastName || ""}`.trim(),
          GivenName: user.GivenName || user.FirstName,
          FirstName: user.FirstName || user.GivenName || "",
          LastName: user.LastName || "",
          OrgId: user.OrgId || "",
          Created: user.Created || new Date().toISOString(),
          CreatedBy: user.CreatedBy || 0,
          Status: user.Status !== undefined ? user.Status : true,
          EntityNo: user.EntityNo,
          ImagePath: user.ImagePath,
          LastUsed: user.LastUsed,
          Changed: user.Changed,
          ChangedBy: user.ChangedBy,
        }));
      },
      providesTags: ["Users"],
    }),

    getCurrentUser: builder.query<CurrentUser, void>({
      query: () => ({
        url: "/v2/crm/me",
        method: "GET",
      }),
      providesTags: ["CurrentUser"],
    }),

    getEmployeeById: builder.query<Employee, number>({
      query: (id) => ({
        url: `/v1/external-user/get-employee/${id}`,
        method: "GET",
      }),
      providesTags: (result, error, id) => [{ type: "Employee", id }],
    }),

    // MS Graph Delta Key endpoints (from b2bUserApi.ts)
    getMsGraphDeltaKey: builder.query<string, void>({
      query: () => ({
        url: "/v1/business/user/get-ms-delta-key",
        method: "GET",
      }),
      transformResponse: (response: { token: string }) => response.token,
      providesTags: ["MsDeltaKey"],
    }),

    postMsGraphDeltaKey: builder.mutation<void, string>({
      query: (key) => ({
        url: "/v1/business/user/post-ms-delta-key",
        method: "POST",
        params: { key },
      }),
      invalidatesTags: ["MsDeltaKey"],
    }),
    getExternalUser: builder.query<BaseUser, number>({
      query: (externalUserNo) => ({
        url: `/v1/external-user/get-external-user/${externalUserNo}`,
        method: "GET",
        providesTags: (result: never) =>
          result
            ? [{ type: "ExternalUser", id: externalUserNo }]
            : ["ExternalUser"],
      }),
      keepUnusedDataFor: 60 * 60 * 12,
    }),
    getExternalUsers: builder.query<BaseUser[], number[]>({
      query: (externalUserNos) => ({
        url: `/v1/external-user/get-external-users`,
        method: "POST",
        body: externalUserNos,
      }),
      async onQueryStarted(externalUserNos, { queryFulfilled, dispatch }) {
        try {
          const { data } = await queryFulfilled;
          data.forEach((user) => {
            dispatch(
              userApi.util.upsertQueryData("getExternalUser", user.Id, user)
            );
          });
        } catch (error) {
          console.error(error);
        }
      },
      keepUnusedDataFor: 60 * 60 * 12,
    }),
  }),
});

// Export hooks
export const {
  useGetUsersWithRoleQuery,
  useGetCurrentUserQuery,
  useGetEmployeeByIdQuery,
  useGetMsGraphDeltaKeyQuery,
  usePostMsGraphDeltaKeyMutation,
  useLazyGetMsGraphDeltaKeyQuery,
} = userApi;

// Re-export types for backward compatibility
export { UserRoleType } from "../types/user.types";
export type {
  ContactPersonData,
  CurrentUser,
  CurrentUserRole,
  Employee,
  Role,
  Team,
  User,
  UserRole,
  UserTeam,
} from "../types/user.types";

// Export additional types
export { getRoleColor, getRoleDisplayName, transformSecExternalUserToUser };
